import { useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Hooks
import useAuth from "utils/hooks/useAuth";
import useGetPersonLSPM from "utils/hooks/useGetPersonLSPM";
import { useAppSelector } from "app/hooks";
import { steps } from "components/PaceLabsInsights/usePaceLabsInsights";
import useRedirectOnNoPersonId from "utils/hooks/useRedirectOnNoPersonId";

import { BreadcrumbStep } from "../../components/PaceLabsDashboard/Breadcrumbs";
import { getReportStatus, getIsFetchingReportStatus } from "store/slice/Pace";
import { urlPaths } from "enums/urlPaths";

const findBreadcrumbStep = (chartName: string | undefined) => {
    let step = null;
    let child = null;

    for (let i = 0; i < steps.length; i++) {
        if (steps[i].route === chartName) {
            step = i;
            break;
        } else if (steps[i].children && steps[i].children?.length) {
            for (let j = 0; j < (steps[i].children as BreadcrumbStep[]).length; j++) {
                if ((steps[i].children as BreadcrumbStep[])[j].route === chartName) {
                    step = i;
                    child = j;
                    break;
                }
            }
        }
    }

    return step !== null
        ? child !== null
            ? {
                  stepSelected: { ...steps[step], children: [(steps[step].children as BreadcrumbStep[])[child]] },
                  chartSelected: (steps[step].children as BreadcrumbStep[])[child],
                  childIndex: child,
              }
            : { stepSelected: { ...steps[step], children: null }, chartSelected: steps[step], childIndex: null }
        : { stepSelected: null, chartSelected: null, childIndex: null };
};

const useMyExperience = (): {
    person_id: number | undefined;
    pathname: string;
    steps: BreadcrumbStep[];
    chartName: string | undefined;
    stepSelected: BreadcrumbStep | null;
    chartSelected: BreadcrumbStep | null;
    rootStepPosition: number;
    transitionTo: React.MutableRefObject<"slide-left" | "slide-right" | "slide-up" | "slide-down">;
    shouldShowAlertMessage: boolean;
    hasCompletedTheAssessment: boolean;
    onClickNavigateMainBreadcrumb: (goToStep: BreadcrumbStep, parentStep?: BreadcrumbStep) => void;
    navigateToMyDashboard: () => void;
} => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const paceAssessmentStatus = useAppSelector(getReportStatus);
    const paceAssessmentRequestStatus = useAppSelector(getIsFetchingReportStatus);

    const hasCompletedTheAssessment = Boolean(
        paceAssessmentStatus?.user_assessments.length && paceAssessmentStatus?.user_assessments[0]?.meta.completed
    );

    const shouldShowAlertMessage =
        (!paceAssessmentStatus?.user_assessments.length ||
            paceAssessmentStatus?.user_assessments[0]?.meta.completed === null) &&
        paceAssessmentRequestStatus === "done";

    // Request LSPM Data to enable or disable My Insights button on Sidebar Menu
    // Also to get the PACE data needed on these pages.
    useGetPersonLSPM(true);

    // Redirect to Dashboard if no personId on Session
    useRedirectOnNoPersonId();

    // Dashboard
    const { loggedInUserData, sessionData, person_id } = useAuth();

    const selectedOrg = sessionData?.authn.user.org_selected;
    const groupId = selectedOrg?.group_id;

    // Insights
    const { chartName } = useParams();
    const { stepSelected, chartSelected } = findBreadcrumbStep(chartName);
    const rootStepPosition = steps.findIndex((innerStep) => innerStep.route === stepSelected?.route);
    const transitionTo = useRef<"slide-left" | "slide-right" | "slide-up" | "slide-down">("slide-right");

    const navigateToMyDashboard = () => {
        navigate(urlPaths.PaceLabsDashboard);
    };

    useEffect(() => {
        if (pathname.includes("my-insights") && !chartName) {
            navigate("/pace/my-insights/balance");
        }
    }, [pathname, chartName, navigate]);

    const onClickNavigateMainBreadcrumb = (goToStep: BreadcrumbStep, parentStep?: BreadcrumbStep) => {
        if (goToStep.route !== chartName) {
            const currentIndex = steps.findIndex((innerStep) => innerStep.route === stepSelected?.route);

            if (!parentStep) {
                const newChartIndex = steps.findIndex((innerStep) => innerStep.route === goToStep.route);
                const currentChartIsChildren = Boolean(
                    goToStep.children?.find((innerStep) => innerStep.route === chartName)
                );

                transitionTo.current = currentChartIsChildren
                    ? "slide-down"
                    : newChartIndex > currentIndex
                    ? "slide-left"
                    : "slide-right";
            } else {
                const isChild = Boolean(parentStep.children?.find((innerStep) => innerStep.route === goToStep.route));
                const newChartIndex = steps.findIndex((innerStep) => innerStep.route === parentStep.route);

                if (isChild) {
                    transitionTo.current = "slide-up";
                } else {
                    transitionTo.current = newChartIndex > currentIndex ? "slide-right" : "slide-left";
                }
            }
        }

        navigate(`/pace/my-insights/${goToStep.route}`);
    };

    return {
        person_id,
        pathname,
        steps,
        chartName,
        stepSelected,
        chartSelected,
        rootStepPosition,
        transitionTo,
        shouldShowAlertMessage,
        hasCompletedTheAssessment,
        onClickNavigateMainBreadcrumb,
        navigateToMyDashboard,
    };
};

export default useMyExperience;
